import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useEffect, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export const Route = createFileRoute("/app/settings/policy")({
  head: () => ({
    meta: buildAppMeta({
      title: "Cancellation policy — CoachQuick",
      description: "Set your cancellation, no-show, and reschedule rules.",
      path: "/app/settings/policy",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/settings/policy` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <PolicyPage />
    </RequireAuth>
  ),
});

type Policy = {
  window_hours: number;
  in_window_action: "refund_credit" | "consume_credit";
  out_of_window_action: "refund_credit" | "consume_credit";
  no_show_fee_cents: number;
  no_show_grace_minutes: number;
  reschedule_window_hours: number;
};

const DEFAULT: Policy = {
  window_hours: 24,
  in_window_action: "refund_credit",
  out_of_window_action: "consume_credit",
  no_show_fee_cents: 0,
  no_show_grace_minutes: 30,
  reschedule_window_hours: 24,
};

function PolicyPage() {
  const { user } = useAuth();
  const [policy, setPolicy] = useState<Policy>(DEFAULT);
  const [feeDollars, setFeeDollars] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("coach_profile")
        .select("cancellation_policy")
        .eq("coach_id", user.id)
        .maybeSingle();
      const p = { ...DEFAULT, ...((data?.cancellation_policy as Partial<Policy>) ?? {}) };
      setPolicy(p);
      setFeeDollars(p.no_show_fee_cents / 100);
      setLoading(false);
    })();
  }, [user?.id]);

  async function save() {
    if (!user) return;
    setSaving(true);
    const payload: Policy = { ...policy, no_show_fee_cents: Math.round(feeDollars * 100) };
    const { error } = await supabase
      .from("coach_profile")
      .upsert({ coach_id: user.id, cancellation_policy: payload }, { onConflict: "coach_id" });
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Policy saved. New policy applies to sessions scheduled from today forward.");
  }

  if (loading) {
    return (
      <AppShell>
        <Card className="p-6">Loading…</Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Cancellation policy</h1>
          <p className="text-sm text-muted-foreground">
            These rules apply when you tap Cancel, No-show, or Reschedule on a session. You always
            confirm with a tap — nothing charges automatically.
          </p>
        </div>

        <Card className="p-6 space-y-5">
          <div>
            <Label>Cancel window (hours before lesson)</Label>
            <Input
              type="number"
              min={0}
              value={policy.window_hours}
              onChange={(e) =>
                setPolicy({ ...policy, window_hours: parseInt(e.target.value || "0", 10) })
              }
            />
            <p className="text-xs text-muted-foreground mt-1">
              Cancellations made at least this many hours ahead are "in-window".
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>In-window cancel</Label>
              <Select
                value={policy.in_window_action}
                onValueChange={(v) =>
                  setPolicy({ ...policy, in_window_action: v as Policy["in_window_action"] })
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="refund_credit">Refund the credit</SelectItem>
                  <SelectItem value="consume_credit">Still consume the credit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Out-of-window cancel</Label>
              <Select
                value={policy.out_of_window_action}
                onValueChange={(v) =>
                  setPolicy({ ...policy, out_of_window_action: v as Policy["out_of_window_action"] })
                }
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="consume_credit">Consume the credit (default)</SelectItem>
                  <SelectItem value="refund_credit">Refund the credit (lenient)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>No-show fee ($)</Label>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={feeDollars}
                onChange={(e) => setFeeDollars(parseFloat(e.target.value || "0"))}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Suggested charge when you mark a no-show. You can always waive.
              </p>
            </div>
            <div>
              <Label>No-show grace (minutes past start)</Label>
              <Input
                type="number"
                min={0}
                value={policy.no_show_grace_minutes}
                onChange={(e) =>
                  setPolicy({ ...policy, no_show_grace_minutes: parseInt(e.target.value || "0", 10) })
                }
              />
            </div>
          </div>

          <div>
            <Label>Reschedule window (hours before lesson)</Label>
            <Input
              type="number"
              min={0}
              value={policy.reschedule_window_hours}
              onChange={(e) =>
                setPolicy({ ...policy, reschedule_window_hours: parseInt(e.target.value || "0", 10) })
              }
            />
            <p className="text-xs text-muted-foreground mt-1">
              Reschedules never charge — this just decides whether to flag it on your dashboard.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save policy"}
            </Button>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
